import { useState, useEffect } from 'react'
import {
  MdLuggage,
  MdEdit,
  MdArrowBack,
  MdPerson,
  MdFlight,
  MdBusiness,
  MdScale,
  MdStraighten,
  MdSchedule,
  MdLocationOn,
  MdDescription,
  MdPhone,
  MdSecurity,
  MdTimeline,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, Badge, InfoCard } from '../../components/ui'
import { useBaggage } from '../../hooks/useBaggage'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function BaggageDetailsPage() {
  const dispatch = useDispatch()
  const { baggageId } = useParams()
  const navigate = useNavigate()
  const [baggage, setBaggage] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { getBaggageAsync, isGetBaggageLoading } = useBaggage()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const loadBaggage = async () => {
    try {
      setIsLoading(true)
      const baggageData = await getBaggageAsync(baggageId)
      console.log('#baggageData:', JSON.stringify(baggageData, null, 2))
      setBaggage(baggageData)
    } catch (error) {
      console.error('Error loading baggage:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (baggageId) {
      loadBaggage()

      const channelName = getWebsocketChannel(baggageId).baggageDetails

      if (channelName) {
        subscribeToChannel(channelName)
        dispatch(addSubscription(channelName))
      }

      return () => {
        if (channelName) {
          unsubscribeFromChannel(channelName)
          dispatch(removeSubscription(channelName))
        }
      }
    }
  }, [baggageId]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatWeight = (weight) => {
    return `${weight} kg`
  }

  const formatDimensions = (dimensions) => {
    if (!dimensions) return 'N/A'
    return `${dimensions.length} × ${dimensions.width} × ${dimensions.height} ${dimensions.unit}`
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleEditBaggage = () => {
    navigate(`/baggages/${baggageId}/edit`)
  }

  const handleBackToBaggage = () => {
    navigate(-1)
  }

  if (isLoading || isGetBaggageLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!baggage) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Baggage Not Found
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The baggage you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="primary" onClick={handleBackToBaggage}>
            Back to Baggage
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left mb-4">
          <Button
            variant="ghost"
            onClick={handleBackToBaggage}
            className="flex items-center space-x-2"
          >
            <MdArrowBack className="text-lg" />
            <span>Back to Baggage</span>
          </Button>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="text-left">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                  Baggage #{baggage.tagNumber?.slice(4)}
                </h1>
                <Badge variant="info">
                  <div className="flex items-center gap-1">
                    <MdLuggage className="text-sm" />
                    <span>Checked</span>
                  </div>
                </Badge>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400">
                {baggage.passenger?.firstName} {baggage.passenger?.lastName} •{' '}
                {baggage.flight?.number} • {formatWeight(baggage.weight)}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={handleEditBaggage}
            className="flex items-center space-x-2"
          >
            <MdEdit className="text-lg" />
            <span>Edit Baggage</span>
          </Button>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <InfoCard title="Baggage Information" icon={MdLuggage}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Baggage ID</span>
              <span className="font-medium text-neutral-900 dark:text-white">{baggage._id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Weight</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatWeight(baggage.weight)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Dimensions</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatDimensions(baggage.dimensions)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Created</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatDate(baggage.createdAt)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Updated</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatDate(baggage.updatedAt)}
              </span>
            </div>
          </div>
        </InfoCard>

        <InfoCard title="Passenger Information" icon={MdPerson}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Name</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.passenger?.firstName} {baggage.passenger?.lastName}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Email</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.passenger?.email}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Phone</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.passenger?.phone}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Passport</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.passenger?.passport?.number}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Country</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.passenger?.passport?.country}
              </span>
            </div>
            {baggage.passenger?._id && (
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/passengers/${baggage.passenger._id}`)}
                  className="w-full flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <MdPerson className="text-sm" />
                  View Passenger Details
                </Button>
              </div>
            )}
          </div>
        </InfoCard>

        <InfoCard title="Flight Information" icon={MdFlight}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Flight Number</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.flight?.number}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Status</span>
              <Badge variant="success">{baggage.flight?.status}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Departure</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.flight?.departure ? formatDateTime(baggage.flight.departure) : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Arrival</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {baggage.flight?.arrival ? formatDateTime(baggage.flight.arrival) : 'N/A'}
              </span>
            </div>
            {baggage.flight?._id && (
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/flights/${baggage.flight._id}`)}
                  className="w-full flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <MdFlight className="text-sm" />
                  View Flight Details
                </Button>
              </div>
            )}
          </div>
        </InfoCard>
      </div>

      {/* Airline Information */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdBusiness className="text-primary-600 dark:text-primary-400" />
          Airline Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdBusiness className="text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Airline Details
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="font-medium text-neutral-900 dark:text-white">
                {baggage.flight?.airline?.name}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Code: {baggage.flight?.airline?.code}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Country: {baggage.flight?.airline?.country}
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdPhone className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Contact Information
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="text-neutral-600 dark:text-neutral-400">
                Phone: {baggage.flight?.airline?.phone}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Founded:{' '}
                {baggage.flight?.airline?.founded
                  ? formatDate(baggage.flight.airline.founded)
                  : 'N/A'}
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdLocationOn className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Headquarters
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="text-neutral-600 dark:text-neutral-400">
                City: {baggage.flight?.airline?.headquarters?.city}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Country: {baggage.flight?.airline?.headquarters?.country}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Address: {baggage.flight?.airline?.headquarters?.address}
              </div>
            </div>
          </div>
        </div>
        {baggage.flight?.airline?._id && (
          <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <Button
              variant="ghost"
              onClick={() => navigate(`/airlines/${baggage.flight.airline._id}`)}
              className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <MdBusiness className="text-sm" />
              View Airline Details
            </Button>
          </div>
        )}
      </div>

      {/* Passport Information */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdSecurity className="text-primary-600 dark:text-primary-400" />
          Passport Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdDescription className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Passport Details
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Number</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {baggage.passenger?.passport?.number}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Country</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {baggage.passenger?.passport?.country}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdSchedule className="text-warning-600 dark:text-warning-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Validity Period
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Issue Date</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {baggage.passenger?.passport?.issueDate
                    ? formatDate(baggage.passenger.passport.issueDate)
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Expiry Date</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {baggage.passenger?.passport?.expiryDate
                    ? formatDate(baggage.passenger.passport.expiryDate)
                    : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Baggage Dimensions and Weight Details */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdStraighten className="text-primary-600 dark:text-primary-400" />
          Physical Specifications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdScale className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Weight
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {formatWeight(baggage.weight)}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdStraighten className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Length
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {baggage.dimensions?.length || 'N/A'} {baggage.dimensions?.unit}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdStraighten className="text-warning-600 dark:text-warning-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Width
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {baggage.dimensions?.width || 'N/A'} {baggage.dimensions?.unit}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdStraighten className="text-error-600 dark:text-error-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Height
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {baggage.dimensions?.height || 'N/A'} {baggage.dimensions?.unit}
            </p>
          </div>
        </div>
        <div className="mt-6 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <MdStraighten className="text-primary-600 dark:text-primary-400" />
            <span className="font-medium text-neutral-900 dark:text-white">Total Dimensions</span>
          </div>
          <p className="text-lg text-neutral-600 dark:text-neutral-400">
            {formatDimensions(baggage.dimensions)}
          </p>
        </div>
      </div>

      {/* Timeline Information */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdTimeline className="text-primary-600 dark:text-primary-400" />
          Timeline Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdSchedule className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Creation Timeline
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Created</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {formatDateTime(baggage.createdAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Last Updated</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {formatDateTime(baggage.updatedAt)}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdFlight className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Flight Timeline
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Departure</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {baggage.flight?.departure ? formatDateTime(baggage.flight.departure) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Arrival</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {baggage.flight?.arrival ? formatDateTime(baggage.flight.arrival) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BaggageDetailsPage
